import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../SiteSettingsContext';
import { Heart, Stethoscope, Globe, Users, ArrowRight, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';

const MissionInfoPage = () => {
  const { logoUrl, navIconUrl, orgName, infoDescription, infoAboutText, infoImageUrl, statsBgUrl } = useSiteSettings();
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className="h-10 w-auto object-contain" />
            ) : (
              <>
                <div className="w-9 h-9 rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center">
                  {navIconUrl ? (
                    <img src={navIconUrl} alt={orgName} className="w-full h-full object-contain" />
                  ) : (
                    <Stethoscope className="w-5 h-5 text-white" />
                  )}
                </div>
                <span className="font-heading font-bold text-lg text-slate-800">{orgName}</span>
              </>
            )}
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-primary">Home</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-sky-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 border border-slate-200 rounded-full mb-6 shadow-sm">
            <Heart className="w-4 h-4 text-rojo" />
            <span className="text-sm font-medium text-slate-700">Medical Missions Since 1988</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-slate-800 mb-6 leading-tight">
            Changing Lives Through<br /><span className="text-primary">Surgical Care</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed mb-10">
            {infoDescription}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" variant="outline" className="rounded-full border-primary text-primary hover:bg-primary/5 px-8" data-testid="info-signup-btn">
                <Users className="w-4 h-4 mr-2" />First Time Sign Up
              </Button>
            </Link>
            <Link to="/returning">
              <Button size="lg" className="rounded-full bg-rojo hover:bg-rojo-dark px-8" data-testid="info-returning-btn">
                Returning Participant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative overflow-hidden">
        {statsBgUrl && (
          <img 
            src={statsBgUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className={`absolute inset-0 ${statsBgUrl ? 'bg-black/40' : 'bg-slate-800'}`} />
        <div className="relative max-w-4xl mx-auto px-6 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          {[
            { value: '3,000+', label: 'Surgeries Performed' },
            { value: '35+', label: 'Years of Service' },
            { value: '175+', label: 'Procedures Per Mission' },
          ].map(s => (
            <div key={s.label}>
              <p className="text-3xl font-heading font-bold text-secondary">{s.value}</p>
              <p className="text-sm text-white/80 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mission Details */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-heading font-bold text-slate-800 mb-8 text-center">About the Mission</h2>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-5 text-slate-600 leading-relaxed overflow-hidden break-words">
              {infoAboutText.split('\n\n').map((para, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
              ))}
            </div>
            <div className="space-y-4">
              <img
                src={infoImageUrl}
                alt="Mission photo"
                className="rounded-xl w-full aspect-[3/4] object-cover shadow-md"
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary/10 rounded-xl p-4 text-center">
                  <Globe className="w-5 h-5 text-secondary mx-auto mb-1" />
                  <p className="font-semibold text-slate-800 text-sm">Antigua, Guatemala</p>
                </div>
                <div className="bg-rojo/10 rounded-xl p-4 text-center">
                  <Calendar className="w-5 h-5 text-rojo mx-auto mb-1" />
                  <p className="font-semibold text-slate-800 text-sm">October Each Year</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-heading font-bold text-slate-800 mb-2 text-center">Who We Need</h2>
          <p className="text-slate-500 text-center mb-10">We welcome medical volunteers across all surgical specialties.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { label: 'Surgeons', desc: 'General, GYN, Urogyn, Peds, Podiatry, GI, Plastics, Hand', color: 'bg-orange-50 border-orange-200' },
              { label: 'Anesthesiology', desc: 'Anesthesiologists & CRNAs', color: 'bg-yellow-50 border-yellow-200' },
              { label: 'OR Nurses and Techs', desc: 'Circulating nurses, scrub nurses, and surgical techs', color: 'bg-blue-50 border-blue-200' },
              { label: 'PACU Nurses', desc: 'Post-anesthesia care and recovery nurses', color: 'bg-green-50 border-green-200' },
            ].map(r => (
              <div key={r.label} className={`rounded-xl border p-5 ${r.color}`}>
                <p className="font-semibold text-slate-800 mb-1">{r.label}</p>
                <p className="text-sm text-slate-600">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-16 text-center">
        <div className="max-w-lg mx-auto px-6">
          <h2 className="text-2xl font-heading font-bold text-slate-800 mb-4">Ready to Join?</h2>
          <p className="text-slate-600 mb-8">Choose how you'd like to proceed.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" variant="outline" className="rounded-full border-primary text-primary px-8 w-full sm:w-auto">
                First Time Sign Up
              </Button>
            </Link>
            <Link to="/returning">
              <Button size="lg" className="rounded-full bg-rojo hover:bg-rojo-dark px-8 w-full sm:w-auto">
                Returning Participant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MissionInfoPage;
